import Cookies from 'js-cookie';

import WindowService from '@src/services/window.service';
import { AffiliateCode } from '@src/constants/affiliate';
import { PlanCode } from '@src/helpers/plan-constants.helper';

export interface Cookie {
  name: string;
  value: string;
  expires?: Date;
  path: string;
  secure: boolean;
  samesite?: 'lax' | 'strict' | 'none';
}

export enum CookieNames {
  PENDING_CHECKOUT = 'pending-checkout',
  PENDING_QUOTE = 'pending-quote',
  SELECTED_COVERAGES = 'selected-coverages',
  TICKET = 'ticket',
  PID = 'pid',
}

export interface PendingQuoteCookie {
  userId?: number;
  pet?: {
    id: number;
    name: string;
  };
  address?: {
    id?: number;
    street1: string;
    street2?: string;
    street3?: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
  };
  communicationEmail?: string;
  clinicId?: number;
  recentVisit?: boolean;
  noVet?: boolean;
  customerDateOfBirth?: string;
  authenticationState?: string;
  planCode?: PlanCode;
  affiliateCode?: AffiliateCode;
}

export interface PendingCheckoutCookie {
  customerId: number;
  quoteReference: string;
  paperlessCommunication: boolean;
  pet: {
    photoUrl: string;
    name: string;
  };
  phone: string;
  pets: {
    petId: number;
    clinics: number[];
  }[];
}

export default class CookiesService {
  static get cookieNames() {
    return CookieNames;
  }
  static expireCookie(name: string) {
    Cookies.remove(name);
  }

  static getCookie(name: string): string | undefined {
    return Cookies.get(name);
  }

  static getAllCookies(): Record<string, string> {
    return Cookies.get();
  }

  static getAllCookiesWithPrefix(prefix: string): Record<string, string> {
    const cookies = CookiesService.getAllCookies();

    if (cookies) {
      return Object.fromEntries(
        Object.entries(cookies).filter(([key]) => key.startsWith(prefix)),
      );
    }

    return {};
  }

  static getPendingCheckoutCookie(): PendingCheckoutCookie | null {
    const cookie = CookiesService.getCookie(
      CookiesService.cookieNames.PENDING_CHECKOUT,
    );

    return cookie ? (JSON.parse(cookie) as PendingCheckoutCookie) : null;
  }

  static getPendingQuoteCookie(userId: number): PendingQuoteCookie | null {
    const cookie = CookiesService.getCookie(
      CookiesService.cookieNames.PENDING_QUOTE,
    );
    const parsedCookie = (
      cookie ? JSON.parse(cookie) : null
    ) as PendingQuoteCookie | null;

    if (parsedCookie?.userId) {
      return parsedCookie.userId == userId ? parsedCookie : null;
    }

    return parsedCookie;
  }

  static setCookie(cookie: Omit<Cookie, 'secure' | 'samesite'>) {
    const isLocalHost = WindowService.window.location.hostname === 'localhost';

    // CSR Buddy hosts this webapp in an iframe on a different domain (not
    // www.chewy.com). Unless we set SameSite: none, Secure: true, some
    // browsers started in 2020 silently refusing to set or attach cookies to
    // AJAX requests when we're hosted in an iframe. Here we set those required
    // cookie flags to ensure the cookies will work in a cross-site iframe.
    //
    // We don't do this on localhost because the "Secure" flag would require us
    // to serve localhost via TLS.
    //
    // See more:
    // * https://blog.chromium.org/2019/10/developers-get-ready-for-new.html
    // * https://datatracker.ietf.org/doc/html/draft-west-cookie-incrementalism-00
    Cookies.set(cookie.name, cookie.value, {
      ...(cookie.expires && { expires: cookie.expires }),
      path: cookie.path,
      sameSite: isLocalHost ? 'strict' : 'none',
      secure: !isLocalHost,
    });
  }

  static updatePendingCheckoutCookie(args: PendingCheckoutCookie) {
    const fifteenMinutesFromNow = new Date();
    fifteenMinutesFromNow.setMinutes(fifteenMinutesFromNow.getMinutes() + 15);

    CookiesService.setCookie({
      name: CookiesService.cookieNames.PENDING_CHECKOUT,
      value: JSON.stringify({
        ...args,
      }),
      expires: fifteenMinutesFromNow,
      path: '/',
    });
  }

  static updatePendingQuoteCookieForPet(
    userId: number,
    args: PendingQuoteCookie,
  ) {
    const thirtyMinutesFromNow = new Date();
    thirtyMinutesFromNow.setMinutes(thirtyMinutesFromNow.getMinutes() + 30);

    const cookieValue = JSON.stringify({
      userId,
      ...CookiesService.getPendingQuoteCookie(userId),
      ...args,
    });

    CookiesService.setCookie({
      name: CookiesService.cookieNames.PENDING_QUOTE,
      value: cookieValue,
      expires: thirtyMinutesFromNow,
      path: '/',
    });
  }
}
